import React from 'react';
import get from 'lodash.get';
import clsx from 'clsx';

// Components
import {
  Element,
  Color,
  FontType,
  Size,
  Type,
  Typography,
  Badge,
  Icon,
  MatchHeight
} from '@americanexpress/aqx-components';
import { Sanitizer } from 'acq-components/lib/base-components/Sanitizer/Sanitizer';
import LazyScrollMetricTracker from 'src/universal/components/DTM/LazyScrollMetricTracker';
import AddToolTip from './AddToolTip';
import { StrikethroughOffer } from 'src/universal/components/Strikethrough';
import Tier2Badge from '../Tier1&2Ribbon';

// Utils
import {
  capitalizeButtonText,
  formatSpecialOfferButtonText
} from 'src/universal/utils/formatOfferButton';

// Styles

import styles from './compare-table.scss?modules';

export const pushContent = (content, width) => {
  return {
    content: content || '',
    width: width || '25%'
  };
};

export const getAnnualFee = (card, colorInfoList) => {
  const colorInfoUnit = (colorInfoList || []).find(
    item => item.shortName === card?.shortName
  );
  const disclaimer = colorInfoUnit
    ? colorInfoUnit.rubiksContent?.annualFeeDisclaimer
    : null;
  return pushContent(
    card && (
      <div className={styles.annualFeeWrap}>
        <Typography
          $size={16}
          $family={FontType.BentonBold}
          theme={{ color: Color.Gray06 }}
        >
          {card && card.annualFee}
        </Typography>
        {disclaimer && (
          <div className={styles.annualFeeDisclaimer}>{disclaimer}</div>
        )}
      </div>
    )
  );
};

const getCardOffer = (card, pznOfferList) => {
  return card && pznOfferList.find(item => item.shortName === card.shortName);
};

export const getAprSection = (card, pznOfferList) => {
  const cardOffer = getCardOffer(card, pznOfferList);
  return pushContent(
    card && (
      <div>
        <Typography $size={16} theme={{ color: Color.Gray06 }}>
          {cardOffer && cardOffer.rate}
        </Typography>
      </div>
    )
  );
};

export const getWelcomeOfferSection = ({
  card,
  pznOfferList,
  pznEligibleDtmResult,
  hasOverviewRibbon
}) => {
  const cardOffer = getCardOffer(card, pznOfferList);
  const pznDtm = pznEligibleDtmResult || {};
  /*
  DE602901: why the next line change?
  (1) the original line before the g141 roll-out (5/6/2024) was:
      const shouldRenderDefaultBadge = offerButton && !shouldRenderVariantOfferStyling;
      this says that we set shouldRenderDefaultBadge to true if offerButton is present AND the g141-variant is false.
  (2) After the g141 roll-out.  the g141-variant should always be considered true (without an incoming indicator).  
      As result, shouldRenderDefaultBadge can never be true regardless the value of offerButton.
  (3) technically var shouldRenderDefaultBadge should be removed.  But I am leaving it here for 2 reasons:
      (a) continuity of the logic evolvement for future tracking purpose.
      (b) will be helpful if the defaultBadge gets resurrected down the road.  
  */
  const shouldRenderDefaultBadge = false;
  return pushContent(
    cardOffer && (
      <div
        className={clsx(styles.welcomeOfferSect, {
          [styles.hasOverviewRibbon]: hasOverviewRibbon
        })}
      >
        {pznDtm.needEvent && (
          <LazyScrollMetricTracker
            scrollDtm={pznDtm.dtm}
            offset={200}
            startTrackingAfterPznArrival={true}
          />
        )}
        <Element>
          <MatchHeight name="overview-offer-badge">
            {shouldRenderDefaultBadge ? (
              <Badge $type={Type.Secondary}>
                <Sanitizer
                  safelySetInnerHTML={capitalizeButtonText(
                    formatSpecialOfferButtonText(cardOffer?.offerButton ?? '')
                  )}
                  className={styles.offerBadgeText}
                />
              </Badge>
            ) : (
              <Tier2Badge
                shortName={card.shortName}
                styles={styles}
                defaultOfferText={card.defaultOfferButtonText}
                isHeader={false}
              />
            )}
          </MatchHeight>
          <Typography
            className={styles.innerHeader}
            theme={{ paddingBottom: 15, color: Color.Gray06 }}
          >
            <StrikethroughOffer header={cardOffer.header} />
          </Typography>
          <Typography $size={16} theme={{ color: Color.Gray06 }}>
            <Sanitizer safelySetInnerHTML={cardOffer.description} />
          </Typography>
        </Element>
      </div>
    )
  );
};

const getCardFeatures = card => {
  return (
    (card &&
      get(card, 'cardFeatures.content').find(item => item.key === 'howToEarn')) ||
    []
  );
};

export const getWhatToEarnRow = card => {
  let whatToEarnIcon = get(card, 'cardFeatures.content[0].icon') || '';

  return pushContent(
    card && (
      <div>
        <Element className={styles.whatToEarnContent}>
          <Icon
            directory="dls"
            imageName={whatToEarnIcon}
            $size={Size.MD}
            theme={{ color: Color.BrightBlue, marginRight: 5, marginTop: 5 }}
          />
          {card.cardFeatures.content.map((feature, index) => {
            return (
              <div className={styles.cardFeature} key={index}>
                <Typography className={styles.innerHeader}>
                  <Sanitizer safelySetInnerHTML={feature && feature.header} />
                </Typography>
              </div>
            );
          })}
        </Element>
      </div>
    )
  );
};

export const getHowToEarnRow = card => {
  const features = getCardFeatures(card);
  return pushContent(
    card && (
      <div>
        {features.content.map((feature, index) => {
          return (
            <div key={index} className={styles.howToEarn}>
              <Typography
                className={styles.innerHeader}
                theme={{
                  paddingBottom: feature.header ? 15 : 0,
                  color: Color.Gray06
                }}
              >
                <Sanitizer safelySetInnerHTML={feature.header} />
                {feature.tag && (
                  <Sanitizer
                    className={styles.flag}
                    safelySetInnerHTML={feature.tag || ''}
                  />
                )}
              </Typography>
              <Typography
                $size={16}
                theme={{ paddingBottom: 15, color: Color.Gray06 }}
              >
                <Sanitizer safelySetInnerHTML={feature.description} />
              </Typography>
            </div>
          );
        })}
      </div>
    )
  );
};

// Compare Benefits section:
const benefitIcon = cardHasHighlight => {
  return (
    <div className={styles.benefitIcon}>
      <Icon
        directory="dls"
        imageName={
          cardHasHighlight ? 'dls-icon-check-filled' : 'dls-icon-close-filled'
        }
        $size={Size.MD}
        theme={{
          color: cardHasHighlight ? Color.BrightBlue : Color.Red,
          marginRight: 5
        }}
      />
    </div>
  );
};

const getDesktopHeaders = defaultValues => {
  const text = get(defaultValues, 'desc[0].text') || null;
  return [
    pushContent(
      <Typography
        $size={20}
        $lineHeight={28}
        theme={{
          color: Color.DeepBlue
        }}
      >
        <Sanitizer safelySetInnerHTML={defaultValues.header} />
        {defaultValues.tag && (
          <Sanitizer
            className={styles.flag}
            safelySetInnerHTML={defaultValues.tag || ''}
          />
        )}
      </Typography>,
      '23%'
    ),
    pushContent(
      text && <AddToolTip text={text} benefitName={defaultValues.header} />,
      '2%'
    )
  ];
};

export const getBenefitsSection = (
  selectedCards,
  highlights,
  currentPermutations,
  isMobile
) => {
  const cells = [];
  const allHighlights = {};

  const filteredCards = isMobile
    ? [
        selectedCards[currentPermutations[0] - 1],
        selectedCards[currentPermutations[1] - 1]
      ]
    : selectedCards;

  filteredCards.forEach(card => {
    if (!card) {
      return;
    }
    const { cardHighlights, shortName } = card;
    cardHighlights.forEach(highlight => {
      const highlightName = highlight.key;
      if (allHighlights[highlightName]) {
        allHighlights[highlightName].cards[shortName] = true;
      } else {
        let defaultValues = highlights.sections.find(
          item => item.key === highlightName
        );
        allHighlights[highlightName] = {
          key: defaultValues.key,
          header: defaultValues.header,
          desc: defaultValues.desc,
          tag: defaultValues.tag,
          cards: {
            [shortName]: true
          }
        };
        cells.push(getDesktopHeaders(defaultValues));
      }
    });
  });

  Object.entries(allHighlights).forEach(
    ([highlightName, highlightContent], highlightIndex) => {
      filteredCards.forEach((card, cardIndex) => {
        if (!card) {
          cells[highlightIndex][cardIndex + 2] = pushContent();
          return;
        }
        const cardHasHiglight = highlightContent.cards[card.shortName];
        if (cardHasHiglight) {
          const hlText =
            card.cardHighlights.find(item => item.key === highlightName).text || '';
          const content = pushContent(
            hlText ? (
              <div className={styles.benefitIcon}> {hlText}</div>
            ) : (
              benefitIcon(true)
            )
          );
          cells[highlightIndex][cardIndex + 2] = content;
        } else {
          cells[highlightIndex][cardIndex + 2] = pushContent(benefitIcon(false));
        }
      });
    }
  );

  return {
    cells
  };
};
